import * as Sentry from "@sentry/react";
import { APP_VERSION, LOGIN_ENVIRONMENT } from "./configs";

const DSN = process.env.REACT_APP_SENTRY_DSN;

export function initSentry() {
  if (!DSN) {
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: LOGIN_ENVIRONMENT,
    release: `ngoui@${APP_VERSION}`,
    // Errors only for now - no performance tracing, no session replay.
    tracesSampleRate: 0,
    ignoreErrors: [
      // Benign browser quirk, not an app bug - already swallowed by the
      // window 'error' listener in index.js, but Sentry attaches its own
      // global handlers so it needs to be filtered here too.
      /ResizeObserver loop/,
    ],
  });
}

export function setSentryUser(user) {
  if (!DSN) return;

  Sentry.setUser({
    id: user?.userId,
    username: user?.userName,
    employeeName: user?.employeeName,
  });
}

export function clearSentryUser() {
  if (!DSN) return;

  Sentry.setUser(null);
}

// Header names that must never reach Sentry - Axios errors carry the full
// request config (see error.config / error.response.config), which includes
// the CSRF/bearer tokens injected by getHeaders() in utils/axios.js.
const SENSITIVE_HEADER_NAMES = ["authorization", "x-bearer-token", "x-csrf-token", "cookie"];

function redactHeaders(headers) {
  if (!headers || typeof headers !== "object") return headers;

  const redacted = {};
  Object.keys(headers).forEach((key) => {
    redacted[key] = SENSITIVE_HEADER_NAMES.includes(key.toLowerCase()) ? "[Redacted]" : headers[key];
  });
  return redacted;
}

// Returns a shallow copy of the Axios error with request headers redacted and
// the (large, circular) raw request/response objects dropped, so only the
// original error is ever passed to Sentry, never the sanitized one.
function sanitizeAxiosError(error) {
  if (!error || typeof error !== "object" || (!error.config && !error.response)) {
    return error;
  }

  const sanitized = Object.assign(Object.create(Object.getPrototypeOf(error)), error);

  if (sanitized.config) {
    sanitized.config = { ...sanitized.config, headers: redactHeaders(sanitized.config.headers) };
  }
  if (sanitized.response) {
    sanitized.response = { ...sanitized.response, request: undefined };
    if (sanitized.response.config) {
      sanitized.response.config = {
        ...sanitized.response.config,
        headers: redactHeaders(sanitized.response.config.headers),
      };
    }
  }
  delete sanitized.request;

  return sanitized;
}

export function captureApiError(error, { endpoint, method, status } = {}) {
  if (!DSN) return;

  Sentry.withScope((scope) => {
    if (endpoint) scope.setTag("api.endpoint", String(endpoint));
    if (method) scope.setTag("api.method", String(method).toUpperCase());
    if (status) scope.setTag("api.status", String(status));
    scope.setLevel(status && status < 500 ? "warning" : "error");
    Sentry.captureException(sanitizeAxiosError(error));
  });
}

export { Sentry };
